import type { HistorySnapshot } from "../history";
import type {
  Block,
  BlockOf,
  BlockType,
  Column,
  EditorMode,
  EmailDocument,
  EmailSettings,
  Group,
  Row,
  SaveStatus,
  Selection,
  TemplateId,
  TextBlockTarget,
} from "../types";
import type { BlockMultiSelection } from "../selection/block-multi-selection";
import type { RowMultiSelection } from "../selection/row-multi-selection";
import type { RowInsertTarget } from "../mutations/index";
import type { ColumnPresetId } from "../column-presets";
import type { SnippetId } from "../snippets";

export type EditorSliceState = {
  document: EmailDocument;
  templateId: TemplateId;
  selection: Selection;
  selectedBlockIds: string[];
  primaryBlockId: string | null;
  anchorBlockId: string | null;
  selectedRowIds: string[];
  primaryRowId: string | null;
  anchorRowId: string | null;
  blockClipboard: Block[];
  textEditingBlock: TextBlockTarget | null;
  textEditReentryGuard: string | null;
  mode: EditorMode;
  saveStatus: SaveStatus;
  past: HistorySnapshot[];
  future: HistorySnapshot[];
};

export type EditorRootState = {
  editor: EditorSliceState;
};

export const EDITOR_SET_MODE = 'EDITOR/SET_MODE' as const;
export const EDITOR_SET_SELECTION = 'EDITOR/SET_SELECTION' as const;
export const EDITOR_SET_BLOCK_SELECTION =
  'EDITOR/SET_BLOCK_SELECTION' as const;
export const EDITOR_SET_ROW_SELECTION =
  'EDITOR/SET_ROW_SELECTION' as const;
export const EDITOR_SET_TEXT_EDITING_BLOCK =
  'EDITOR/SET_TEXT_EDITING_BLOCK' as const;
export const EDITOR_SET_TEXT_EDIT_REENTRY_GUARD =
  'EDITOR/SET_TEXT_EDIT_REENTRY_GUARD' as const;
export const EDITOR_SET_SAVE_STATUS = 'EDITOR/SET_SAVE_STATUS' as const;
export const EDITOR_LOAD_DOCUMENT = 'EDITOR/LOAD_DOCUMENT' as const;
export const EDITOR_UNDO = 'EDITOR/UNDO' as const;
export const EDITOR_REDO = 'EDITOR/REDO' as const;
export const EDITOR_FLUSH_HISTORY = 'EDITOR/FLUSH_HISTORY' as const;
export const EDITOR_UPDATE_SETTINGS = 'EDITOR/UPDATE_SETTINGS' as const;
export const EDITOR_UPDATE_ROW = 'EDITOR/UPDATE_ROW' as const;
export const EDITOR_UPDATE_GROUP = 'EDITOR/UPDATE_GROUP' as const;
export const EDITOR_UPDATE_COLUMN = 'EDITOR/UPDATE_COLUMN' as const;
export const EDITOR_UPDATE_BLOCK = 'EDITOR/UPDATE_BLOCK' as const;
export const EDITOR_INSERT_BLOCK = 'EDITOR/INSERT_BLOCK' as const;
export const EDITOR_MOVE_BLOCK = 'EDITOR/MOVE_BLOCK' as const;
export const EDITOR_INSERT_ROW_WITH_BLOCK =
  'EDITOR/INSERT_ROW_WITH_BLOCK' as const;
export const EDITOR_MOVE_BLOCK_TO_NEW_ROW =
  'EDITOR/MOVE_BLOCK_TO_NEW_ROW' as const;
export const EDITOR_INSERT_ROW = 'EDITOR/INSERT_ROW' as const;
export const EDITOR_INSERT_GROUP = 'EDITOR/INSERT_GROUP' as const;
export const EDITOR_INSERT_SNIPPET = 'EDITOR/INSERT_SNIPPET' as const;
export const EDITOR_MOVE_ROW = 'EDITOR/MOVE_ROW' as const;
export const EDITOR_MOVE_TOP_LEVEL_SECTION =
  'EDITOR/MOVE_TOP_LEVEL_SECTION' as const;
export const EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS =
  'EDITOR/MOVE_CONTIGUOUS_TOP_LEVEL_ROWS' as const;
export const EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS =
  'EDITOR/MOVE_CONTIGUOUS_GROUP_ROWS' as const;
export const EDITOR_MOVE_ROW_IN_GROUP = 'EDITOR/MOVE_ROW_IN_GROUP' as const;
export const EDITOR_MOVE_ROW_BETWEEN_PARENTS =
  'EDITOR/MOVE_ROW_BETWEEN_PARENTS' as const;
export const EDITOR_UNGROUP = 'EDITOR/UNGROUP' as const;
export const EDITOR_GROUP_ROWS = 'EDITOR/GROUP_ROWS' as const;
export const EDITOR_DELETE_GROUP = 'EDITOR/DELETE_GROUP' as const;
export const EDITOR_SET_COLUMN_WIDTHS = 'EDITOR/SET_COLUMN_WIDTHS' as const;
export const EDITOR_ADD_COLUMN = 'EDITOR/ADD_COLUMN' as const;
export const EDITOR_REMOVE_COLUMN = 'EDITOR/REMOVE_COLUMN' as const;
export const EDITOR_DUPLICATE_SELECTED = 'EDITOR/DUPLICATE_SELECTED' as const;
export const EDITOR_DELETE_SELECTED = 'EDITOR/DELETE_SELECTED' as const;
export const EDITOR_MOVE_SELECTED = 'EDITOR/MOVE_SELECTED' as const;
export const EDITOR_COPY_SELECTED_BLOCKS =
  'EDITOR/COPY_SELECTED_BLOCKS' as const;
export const EDITOR_CUT_SELECTED_BLOCKS =
  'EDITOR/CUT_SELECTED_BLOCKS' as const;
export const EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD =
  'EDITOR/PASTE_BLOCKS_FROM_CLIPBOARD' as const;

export type EditorAction =
  | { type: typeof EDITOR_SET_MODE; payload: EditorMode }
  | { type: typeof EDITOR_SET_SELECTION; payload: Selection }
  | { type: typeof EDITOR_SET_BLOCK_SELECTION; payload: BlockMultiSelection }
  | { type: typeof EDITOR_SET_ROW_SELECTION; payload: RowMultiSelection }
  | {
      type: typeof EDITOR_SET_TEXT_EDITING_BLOCK;
      payload: TextBlockTarget | null;
    }
  | { type: typeof EDITOR_SET_TEXT_EDIT_REENTRY_GUARD; payload: string | null }
  | { type: typeof EDITOR_SET_SAVE_STATUS; payload: SaveStatus }
  | {
      type: typeof EDITOR_LOAD_DOCUMENT;
      payload: { document: EmailDocument; templateId: TemplateId };
    }
  | { type: typeof EDITOR_UNDO }
  | { type: typeof EDITOR_REDO }
  | { type: typeof EDITOR_FLUSH_HISTORY; payload: HistorySnapshot }
  | { type: typeof EDITOR_UPDATE_SETTINGS; payload: Partial<EmailSettings> }
  | {
      type: typeof EDITOR_UPDATE_ROW;
      payload: { rowId: string; patch: Partial<Row> };
    }
  | {
      type: typeof EDITOR_UPDATE_GROUP;
      payload: { groupId: string; patch: Partial<Group> };
    }
  | {
      type: typeof EDITOR_UPDATE_COLUMN;
      payload: { rowId: string; columnId: string; patch: Partial<Column> };
    }
  | {
      type: typeof EDITOR_UPDATE_BLOCK;
      payload: {
        rowId: string;
        columnId: string;
        blockId: string;
        patch: Partial<BlockOf<BlockType>>;
        history?: boolean;
      };
    }
  | {
      type: typeof EDITOR_INSERT_BLOCK;
      payload: {
        rowId: string;
        columnId: string;
        index: number;
        blockType: BlockType;
      };
    }
  | {
      type: typeof EDITOR_MOVE_BLOCK;
      payload: {
        from: {
          rowId: string;
          columnId: string;
          blockId: string;
          blockIds?: string[];
        };
        to: { rowId: string; columnId: string; index: number };
      };
    }
  | {
      type: typeof EDITOR_INSERT_ROW_WITH_BLOCK;
      payload: { target: RowInsertTarget; blockType: BlockType };
    }
  | {
      type: typeof EDITOR_MOVE_BLOCK_TO_NEW_ROW;
      payload: {
        from: { rowId: string; columnId: string; blockId: string };
        target: RowInsertTarget;
      };
    }
  | {
      type: typeof EDITOR_INSERT_ROW;
      payload: { target: RowInsertTarget; presetId: ColumnPresetId };
    }
  | {
      type: typeof EDITOR_INSERT_GROUP;
      payload: { index: number; group?: Group };
    }
  | {
      type: typeof EDITOR_INSERT_SNIPPET;
      payload: { snippetId: SnippetId; index?: number };
    }
  | {
      type: typeof EDITOR_MOVE_ROW;
      payload: { fromIndex: number; toIndex: number };
    }
  | {
      type: typeof EDITOR_MOVE_TOP_LEVEL_SECTION;
      payload: { fromIndex: number; toIndex: number };
    }
  | {
      type: typeof EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS;
      payload: { rowIds: string[]; slotIndex: number };
    }
  | {
      type: typeof EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS;
      payload: { groupId: string; rowIds: string[]; slotIndex: number };
    }
  | {
      type: typeof EDITOR_MOVE_ROW_IN_GROUP;
      payload: { groupId: string; fromIndex: number; toIndex: number };
    }
  | {
      type: typeof EDITOR_MOVE_ROW_BETWEEN_PARENTS;
      payload: {
        rowId: string;
        to:
          | { parent: 'document'; index: number }
          | { parent: 'group'; groupId: string; rowIndex: number };
      };
    }
  | { type: typeof EDITOR_UNGROUP; payload: string }
  | { type: typeof EDITOR_GROUP_ROWS; payload: string[] }
  | {
      type: typeof EDITOR_DELETE_GROUP;
      payload: { groupId: string; mode: 'delete' | 'ungroup' };
    }
  | {
      type: typeof EDITOR_SET_COLUMN_WIDTHS;
      payload: {
        rowId: string;
        widths: Row['columnWidths'];
        history?: boolean;
      };
    }
  | {
      type: typeof EDITOR_ADD_COLUMN;
      payload: { rowId: string; afterColumnId?: string };
    }
  | {
      type: typeof EDITOR_REMOVE_COLUMN;
      payload: { rowId: string; columnId: string };
    }
  | { type: typeof EDITOR_DUPLICATE_SELECTED }
  | { type: typeof EDITOR_DELETE_SELECTED }
  | { type: typeof EDITOR_MOVE_SELECTED; payload: -1 | 1 }
  | { type: typeof EDITOR_COPY_SELECTED_BLOCKS }
  | { type: typeof EDITOR_CUT_SELECTED_BLOCKS }
  | { type: typeof EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD };

export type EditorThunkAction = (
  dispatch: EditorDispatch,
  getState: () => EditorRootState,
) => void | Promise<void>;

export type EditorDispatch = {
  (action: EditorAction): EditorAction;
  (action: EditorThunkAction): EditorThunkAction;
};
